import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  File,
  Info,
  Landmark,
  Receipt as ReceiptIcon,
  Smartphone,
  UploadCloud,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox";
import DatePicker from "@/components/ui/DatePicker";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import Textarea from "@/components/ui/Textarea";
import type { OfflineMode, Installment } from "@/types/installment";
import { formatDueDate, formatINR } from "../data";

const OFFLINE_MODES: { value: OfflineMode; label: string; icon: typeof Banknote }[] = [
  { value: "Cash", label: "Cash", icon: Banknote },
  { value: "UPI", label: "UPI", icon: Smartphone },
  { value: "Cheque", label: "Cheque", icon: ReceiptIcon },
  { value: "Bank Transfer", label: "Bank Transfer", icon: Landmark },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function toDate(display: string): Date | null {
  if (!display) return null;
  const parts = display.split(" ");
  if (parts.length !== 3) return null;
  const day = Number(parts[0]);
  const mon = MONTHS.indexOf(parts[1]);
  const year = Number(parts[2]);
  if (!day || mon < 0 || !year) return null;
  return new Date(year, mon, day);
}

export interface OfflineCollectionData {
  amountReceived: number;
  mode: OfflineMode;
  reference: string;
  collectionDate: string;
  receipt: string;
  remarks: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  installment: Installment | null;
  onConfirm: (data: OfflineCollectionData) => void;
  loading?: boolean;
}

export default function RecordOfflineModal({ open, onClose, installment, onConfirm, loading }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState<OfflineMode>("Cash");
  const [reference, setReference] = useState("");
  const [collectionDate, setCollectionDate] = useState("");
  const [receiptName, setReceiptName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");

  function reset() {
    setAmount(installment ? String(installment.amount) : "");
    setMode("Cash");
    setReference("");
    setCollectionDate("");
    setReceiptName("");
    setRemarks("");
    setConfirmed(false);
    setError("");
  }

  function handleClose() {
    if (loading) return;
    reset();
    onClose();
  }

  function handleConfirm() {
    const num = Number(amount);
    if (!amount || isNaN(num) || num <= 0) {
      setError("Enter a valid amount received");
      return;
    }
    const date = toDate(collectionDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!date || isNaN(date.getTime())) {
      setError("Select the collection date");
      return;
    }
    if (date.getTime() > today.getTime()) {
      setError("Collection date cannot be in the future");
      return;
    }
    if (mode !== "Cash" && !reference.trim()) {
      setError("Enter the reference number for this mode");
      return;
    }
    onConfirm({
      amountReceived: Math.round(num * 100) / 100,
      mode,
      reference: reference.trim(),
      collectionDate,
      receipt: receiptName,
      remarks: remarks.trim(),
    });
  }

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title="Record offline collection"
        size="md"
        footer={
          <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
            <Button variant="secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} loading={loading} disabled={!confirmed}>
              Record Collection
            </Button>
          </div>
        }
      >
        <div className="flex items-center justify-between">
          <span className="font-mono text-[13px] text-ink-secondary">{installment?.installmentId ?? "—"}</span>
        </div>

        <div className="mt-3 flex items-start gap-3 rounded-lg border border-[#BAE6FD] bg-[#F0F9FF] p-3.5">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-info" />
          <p className="text-[13px] text-ink">
            Use this when you have received the EMI directly from the customer. No platform charge applies to an
            offline collection.
          </p>
        </div>

        <div className="mt-4 rounded-lg border border-line bg-page p-4">
          <p className="text-sm font-semibold text-ink">Installment</p>
          <div className="mt-2 space-y-2.5">
            {[
              { label: "Mandate Reference", value: installment?.refNo ?? "—" },
              { label: "Customer Name", value: installment?.customerName ?? "—" },
              {
                label: "Installment Number",
                value: installment ? `${installment.installmentNumber} of ${installment.totalInstallments}` : "—",
              },
              { label: "Amount Due", value: installment ? formatINR(installment.amount) : "—" },
              { label: "Original Due Date", value: installment ? formatDueDate(installment.dueDate) : "—" },
            ].map((r) => (
              <div key={r.label} className="flex items-center justify-between gap-4">
                <span className="text-[13px] text-ink-secondary">{r.label}</span>
                <span className="text-right text-sm font-medium text-ink">{r.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <Input
            label="Amount Received"
            required
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            prefixIcon={<span className="text-sm text-ink-secondary">₹</span>}
            helper="Edit only if a different amount was received"
          />
        </div>

        <div className="mt-4">
          <span className="mb-1.5 block text-[13px] font-medium text-ink-secondary">
            Collection Mode<span className="ml-0.5 text-danger">*</span>
          </span>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {OFFLINE_MODES.map((m) => {
              const Icon = m.icon;
              const selected = mode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMode(m.value)}
                  className={`flex items-center gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors cursor-pointer ${
                    selected
                      ? "border-[#1E40AF] bg-[#EFF6FF]"
                      : "border-slate-300 bg-white hover:border-slate-400"
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                      selected ? "bg-[#1E40AF] text-white" : "bg-slate-100 text-ink-muted"
                    }`}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <span className={`text-sm font-medium ${selected ? "text-[#1E40AF]" : "text-ink"}`}>
                    {m.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4">
          <Input
            label="Reference Number"
            required={mode !== "Cash"}
            placeholder={mode === "UPI" ? "UPI reference" : mode === "Cheque" ? "Cheque number" : mode === "Bank Transfer" ? "Bank UTR" : "Reference number"}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            helper={mode === "Cash" ? "No reference needed for cash" : "UPI reference, cheque number or bank UTR"}
          />
        </div>

        <div className="mt-4">
          <DatePicker
            label="Collection Date"
            required
            value={collectionDate}
            onChange={(v) => {
              setCollectionDate(v);
              if (v) setError("");
            }}
          />
        </div>

        <div className="mt-4">
          <span className="mb-1.5 block text-[13px] font-medium text-ink-secondary">
            Receipt<span className="text-ink-muted"> (optional)</span>
          </span>
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setReceiptName(f.name);
              e.target.value = "";
            }}
          />
          {receiptName ? (
            <div className="flex items-center gap-3 rounded-lg border border-line bg-page px-3 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white text-ink-muted">
                <File className="h-4 w-4" />
              </span>
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{receiptName}</p>
              <CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
              <button
                type="button"
                onClick={() => setReceiptName("")}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted hover:bg-slate-100 hover:text-danger cursor-pointer"
                aria-label="Remove receipt"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-4 py-5 text-center transition-colors hover:border-primary-400 hover:bg-page cursor-pointer"
            >
              <UploadCloud className="h-6 w-6 text-ink-muted" />
              <span className="text-sm text-ink">Click to upload the receipt</span>
              <span className="text-xs text-ink-secondary">JPG, PNG or PDF, maximum 5 MB</span>
            </button>
          )}
          <p className="mt-1.5 text-xs text-ink-secondary">Photograph of the receipt or payment screenshot</p>
        </div>

        <div className="mt-4">
          <Textarea
            label="Remarks"
            rows={2}
            maxLength={200}
            placeholder="Optional note about this offline collection"
            helper={`${remarks.length}/200 characters`}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
        </div>

        <div className="mt-4 flex items-start gap-3 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-3.5">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <p className="text-[13px] text-ink">
            This record cannot be edited once saved. It does not affect the remaining installments, which will
            continue to be presented to the bank as scheduled.
          </p>
        </div>

        <div className="mt-4">
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            label="I confirm that I have received this amount from the customer."
          />
        </div>

        {error && <p className="mt-3 text-xs text-danger">{error}</p>}
      </Modal>
    </>
  );
}